import type { LatLon, Placement, Vec2 } from '@sw/schema'
import type { EastNorth } from './enu.js'
import { eastNorthToGeo, geoToEastNorth } from './enu.js'
import { bearingDeg, degToRad, distance, normalizeDeg, sub } from './vec.js'

/**
 * World frame ⇄ Earth.
 *
 * Authored geometry lives in a local metric frame and only meets real
 * coordinates here. That boundary is the whole reason a world built on one
 * football field can be dropped onto another: moving a world means editing one
 * placement, not rewriting every emitter.
 *
 * The world's +Y axis points `rotationDeg` clockwise from true north, and +X is
 * a further quarter turn clockwise, so with no rotation the frame reads as
 * east/north.
 */
function rotateWorldToEastNorth(p: Vec2, rotationDeg: number): EastNorth {
  const t = degToRad(rotationDeg)
  const cos = Math.cos(t)
  const sin = Math.sin(t)
  return { east: p.x * cos + p.y * sin, north: -p.x * sin + p.y * cos }
}

function rotateEastNorthToWorld(e: EastNorth, rotationDeg: number): Vec2 {
  const t = degToRad(rotationDeg)
  const cos = Math.cos(t)
  const sin = Math.sin(t)
  return { x: e.east * cos - e.north * sin, y: e.east * sin + e.north * cos }
}

export function worldToGeo(placement: Placement, point: Vec2): LatLon {
  const rotated = rotateWorldToEastNorth(point, placement.rotationDeg)
  return eastNorthToGeo(placement.anchor, {
    east: rotated.east * placement.scale,
    north: rotated.north * placement.scale,
  })
}

export function geoToWorld(placement: Placement, position: LatLon): Vec2 {
  const offset = geoToEastNorth(placement.anchor, position)
  return rotateEastNorthToWorld(
    { east: offset.east / placement.scale, north: offset.north / placement.scale },
    placement.rotationDeg,
  )
}

/**
 * A device reports heading against true north; the engine reasons in the
 * world frame. Rotating a placement must not silently swing every listener's
 * orientation, so the two are converted explicitly.
 */
export function worldHeadingFromTrueNorth(headingDeg: number, placement: Placement): number {
  return normalizeDeg(headingDeg - placement.rotationDeg)
}

export function trueNorthFromWorldHeading(worldHeadingDeg: number, placement: Placement): number {
  return normalizeDeg(worldHeadingDeg + placement.rotationDeg)
}

export interface ReferencePointPair {
  /** A recognisable spot in the authored world, e.g. a field corner. */
  world: Vec2
  /** Where that spot actually is on the ground. */
  geo: LatLon
}

export interface SolvedPlacement {
  anchor: LatLon
  rotationDeg: number
  scale: number
}

/**
 * Solves anchor, rotation and scale from two matched points — the maths behind
 * "tap two corners of your field". Two correspondences fully determine a
 * similarity transform, which is why the wizard asks for exactly two.
 *
 * @throws if the two reference points coincide in either frame, which leaves
 * rotation and scale undetermined.
 */
export function solvePlacement(a: ReferencePointPair, b: ReferencePointPair): SolvedPlacement {
  const worldSpan = distance(a.world, b.world)
  if (worldSpan === 0) {
    throw new Error('The two reference points are the same place in the world; they must differ.')
  }

  // Work in a temporary tangent plane centred on the first reference point.
  const offsetB = geoToEastNorth(a.geo, b.geo)
  const groundSpan = Math.hypot(offsetB.east, offsetB.north)
  if (groundSpan === 0) {
    throw new Error('The two reference points map to the same spot on the ground; they must differ.')
  }

  const scale = groundSpan / worldSpan
  const worldBearing = bearingDeg(a.world, b.world)
  const groundBearing = normalizeDeg((Math.atan2(offsetB.east, offsetB.north) * 180) / Math.PI)
  const rotationDeg = normalizeDeg(groundBearing - worldBearing)

  // Walk back from the first reference point to where the world origin lands.
  const originOffsetInWorld = sub({ x: 0, y: 0 }, a.world)
  const rotated = rotateWorldToEastNorth(originOffsetInWorld, rotationDeg)
  const anchor = eastNorthToGeo(a.geo, {
    east: rotated.east * scale,
    north: rotated.north * scale,
  })

  return { anchor, rotationDeg, scale }
}
